import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

export interface PushDebugInfo {
  platform: string;
  isNative: boolean;
  permissionRequested: boolean;
  permissionResult: string;
  registrationAttempted: boolean;
  registrationSuccess: boolean;
  token: string | null;
  tokenType: string | null;
  tokenSaved: boolean;
  lastError: string | null;
  timestamp: string;
}

type InitOptions = {
  forceRefresh?: boolean;
};

let pushInitInFlight: Promise<void> | null = null;
let pushAutoInitUserId: string | null = null;
let pushListenerHandles: Array<{ remove: () => Promise<void> | void }> = [];
let pushLastKnownFcmToken: string | null = null;
let pushPendingForceRefresh = false;

// Shared module-level debug state so all hook instances see the same result
let sharedDebugInfo: Partial<PushDebugInfo> = {};
const debugListeners = new Set<(info: Partial<PushDebugInfo>) => void>();

const broadcastDebug = (updates: Partial<PushDebugInfo>) => {
  sharedDebugInfo = { ...sharedDebugInfo, ...updates, timestamp: new Date().toISOString() };
  debugListeners.forEach(fn => fn(sharedDebugInfo));
};

const IOS_FCM_TOKEN_MIN_LENGTH = 70;
const IOS_TOKEN_RETRY_DELAY_MS = 2000;
const IOS_TOKEN_WAIT_TIMEOUT_MS = 90000;
const APNS_HEX_TOKEN_REGEX = /^[a-f0-9]{64}$/i;
const TRANSIENT_APNS_ERROR_HINTS = [
  'no apns token specified',
  'apns token has not been set',
  'apns token',
  'nicht rechtzeitig',
  'neu starten',
  'timed out',
];

const isLikelyApnsToken = (token: string | null | undefined) =>
  Boolean(token && APNS_HEX_TOKEN_REGEX.test(token));

const isLikelyFcmToken = (token: string | null | undefined): token is string =>
  Boolean(token && token.length >= IOS_FCM_TOKEN_MIN_LENGTH && !isLikelyApnsToken(token));

const isTransientApnsError = (message: string) => {
  const normalized = message.toLowerCase();
  return TRANSIENT_APNS_ERROR_HINTS.some((hint) => normalized.includes(hint));
};

const clearPushListeners = async () => {
  await Promise.allSettled(
    pushListenerHandles.map(async (handle) => {
      try {
        await handle.remove();
      } catch (error) {
        console.warn('[Push] Failed to remove stale listener:', error);
      }
    })
  );

  pushListenerHandles = [];
};

export const usePushNotifications = () => {
  const { user } = useAuth();
  const [isSupported, setIsSupported] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<'granted' | 'denied' | 'prompt'>('prompt');
  const [debugInfo, setDebugInfo] = useState<PushDebugInfo>({
    platform: Capacitor.getPlatform(),
    isNative: Capacitor.isNativePlatform(),
    permissionRequested: false,
    permissionResult: 'none',
    registrationAttempted: false,
    registrationSuccess: false,
    token: null,
    tokenType: null,
    tokenSaved: false,
    lastError: null,
    timestamp: new Date().toISOString(),
    // Merge any already-completed registration state
    ...sharedDebugInfo
  });

  // Subscribe to shared debug updates from other hook instances
  useEffect(() => {
    const listener = (updates: Partial<PushDebugInfo>) => {
      setDebugInfo(prev => ({ ...prev, ...updates }));
    };
    debugListeners.add(listener);
    return () => { debugListeners.delete(listener); };
  }, []);

  const updateDebug = (updates: Partial<PushDebugInfo>) => {
    setDebugInfo(prev => ({
      ...prev,
      ...updates,
      timestamp: new Date().toISOString()
    }));
    broadcastDebug(updates);
  };

  useEffect(() => {
    const platform = Capacitor.getPlatform();
    const isNative = Capacitor.isNativePlatform();

    setIsSupported(isNative);

    if (!user) {
      pushAutoInitUserId = null;
      return;
    }

    if (isNative && pushAutoInitUserId !== user.id) {
      pushAutoInitUserId = user.id;

      const timer = setTimeout(() => {
        void initializePushNotifications();
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [user]);

  const initializePushNotifications = async ({ forceRefresh = false }: InitOptions = {}) => {
    const platform = Capacitor.getPlatform();

    if (pushInitInFlight) {
      if (forceRefresh) {
        pushPendingForceRefresh = true;
        updateDebug({
          permissionRequested: true,
          registrationAttempted: true,
          lastError: 'Registrazione già in corso. Neuer Versuch startet automatisch direkt danach.'
        });
        console.log('[Push] Force retry requested while initialization is running; scheduling follow-up attempt...');
      } else {
        console.log('[Push] Initialization already in progress, waiting...');
      }

      await pushInitInFlight;

      if (forceRefresh && pushPendingForceRefresh && !pushInitInFlight) {
        pushPendingForceRefresh = false;
        return initializePushNotifications({ forceRefresh: true });
      }

      return;
    }

    pushInitInFlight = (async () => {
      try {
        updateDebug({ permissionRequested: true, registrationAttempted: true, lastError: null });
        console.log(`[Push] Starting initialization on ${platform}${forceRefresh ? ' (force refresh)' : ''}...`);

        if (platform === 'ios') {
          await initializeIOS(forceRefresh);
        } else if (platform === 'android') {
          await initializeAndroid();
        }
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        console.error('[Push] Initialization error:', msg);
        updateDebug({ lastError: msg, registrationSuccess: false });
      }
    })();

    try {
      await pushInitInFlight;
    } finally {
      pushInitInFlight = null;
    }

    if (pushPendingForceRefresh) {
      pushPendingForceRefresh = false;
      await initializePushNotifications({ forceRefresh: true });
    }
  };

  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  /**
   * Wait for FCM token via the tokenReceived listener (primary)
   * or a single getToken() call (fallback trigger).
   * 
   * Key insight: On iOS the token comes from the native MessagingDelegate
   * callback, NOT from polling getToken(). We set up a listener, trigger
   * one getToken() to kick off the exchange, then wait for the listener.
   */
  const waitForFcmToken = async (
    FirebaseMessaging: any,
    getListenerToken: () => string | null,
    maxWaitMs = IOS_TOKEN_WAIT_TIMEOUT_MS,
  ): Promise<string | null> => {
    // 1. Check if we already have a cached token
    if (isLikelyFcmToken(pushLastKnownFcmToken)) {
      console.log('[Push-iOS] Using cached FCM token');
      return pushLastKnownFcmToken;
    }

    const listenerToken = getListenerToken();
    if (isLikelyFcmToken(listenerToken)) {
      console.log('[Push-iOS] Using already-received listener token');
      return listenerToken;
    }

    // 2. Trigger one getToken() call — this kicks Firebase to start the APNs→FCM exchange
    console.log('[Push-iOS] Triggering getToken() to start APNs→FCM exchange...');
    try {
      const tokenResult = await FirebaseMessaging.getToken();
      const directToken = tokenResult?.token;
      if (isLikelyFcmToken(directToken)) {
        pushLastKnownFcmToken = directToken;
        console.log('[Push-iOS] Got FCM token directly from getToken()');
        return directToken;
      }
      if (directToken) {
        console.log(`[Push-iOS] getToken() returned non-FCM token (len=${directToken.length}), waiting for listener...`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.log(`[Push-iOS] getToken() trigger error (expected on first run): ${msg}`);
    }

    // 3. Now wait for the tokenReceived listener to fire with a valid FCM token
    console.log(`[Push-iOS] Waiting up to ${maxWaitMs / 1000}s for FCM token via native callback...`);
    const deadline = Date.now() + maxWaitMs;
    
    while (Date.now() < deadline) {
      // Check cached token (set by listener callback)
      if (isLikelyFcmToken(pushLastKnownFcmToken)) {
        console.log('[Push-iOS] FCM token received via native callback');
        return pushLastKnownFcmToken;
      }

      const lt = getListenerToken();
      if (isLikelyFcmToken(lt)) {
        pushLastKnownFcmToken = lt;
        console.log('[Push-iOS] FCM token received via listener');
        return lt;
      }

      await sleep(IOS_TOKEN_RETRY_DELAY_MS);
    }

    // 4. Final check
    if (isLikelyFcmToken(pushLastKnownFcmToken)) return pushLastKnownFcmToken;
    const finalLt = getListenerToken();
    if (isLikelyFcmToken(finalLt)) return finalLt;

    // 5. One last getToken() attempt
    try {
      console.log('[Push-iOS] Final getToken() attempt...');
      const lastResult = await FirebaseMessaging.getToken();
      if (isLikelyFcmToken(lastResult?.token)) {
        pushLastKnownFcmToken = lastResult.token;
        return lastResult.token;
      }
    } catch (err) {
      console.warn('[Push-iOS] Final getToken() failed:', err);
    }

    throw new Error('FCM token non ricevuto dal callback nativo. Verifica che APNs Key sia configurato correttamente nella Firebase Console.');
  };

  const initializeIOS = async (forceRefresh = false) => {
    try {
      if (!Capacitor.isPluginAvailable('FirebaseMessaging')) {
        const msg = 'FirebaseMessaging plugin not available (run npx cap sync ios and rebuild)';
        console.error('[Push-iOS]', msg);
        updateDebug({ registrationSuccess: false, lastError: msg });
        return;
      }

      const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
      await clearPushListeners();

      let listenerToken: string | null = null;

      if (forceRefresh) {
        console.log('[Push-iOS] Force refresh requested, clearing cached FCM token and asking Firebase for a fresh token...');
        pushLastKnownFcmToken = null;
        updateDebug({
          registrationSuccess: false,
          token: null,
          tokenType: null,
          tokenSaved: false,
          lastError: null
        });

        try {
          await FirebaseMessaging.deleteToken();
          console.log('[Push-iOS] Cleared stale Firebase token before retry');
        } catch (error) {
          console.warn('[Push-iOS] deleteToken failed during forced retry (non-fatal):', error);
        }
      }

      const tokenListener = await FirebaseMessaging.addListener('tokenReceived', async (event) => {
        if (!event?.token) return;

        const incomingToken = String(event.token);
        const tokenPreview = incomingToken.slice(0, 20) + '...';
        listenerToken = incomingToken;
        console.log('[Push-iOS] tokenReceived event:', tokenPreview);

        if (isLikelyFcmToken(incomingToken)) {
          pushLastKnownFcmToken = incomingToken;
          updateDebug({
            token: tokenPreview,
            tokenType: 'FCM',
            registrationSuccess: true,
            lastError: null
          });
          setIsRegistered(true);
          await saveDeviceToken(incomingToken);
          return;
        }

        if (isLikelyApnsToken(incomingToken)) {
          updateDebug({
            token: tokenPreview,
            tokenType: 'APNS',
            lastError: 'APNs token ricevuto, in attesa del token FCM...'
          });
        }
      });

      const notificationReceivedListener = await FirebaseMessaging.addListener('notificationReceived', (notification) => {
        console.log('[Push-iOS] Notification received:', notification);
        toast.info(notification.notification?.title || 'Nuova notifica', {
          description: notification.notification?.body
        });
      });

      const notificationActionListener = await FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
        console.log('[Push-iOS] Notification tapped:', event);
      });

      pushListenerHandles = [tokenListener, notificationReceivedListener, notificationActionListener];

      const existingPermission = typeof FirebaseMessaging.checkPermissions === 'function'
        ? await FirebaseMessaging.checkPermissions()
        : { receive: 'prompt' };

      const permResult = existingPermission.receive === 'granted'
        ? existingPermission
        : await FirebaseMessaging.requestPermissions();

      console.log('[Push-iOS] Permission result:', permResult.receive);
      updateDebug({ permissionResult: permResult.receive });

      if (permResult.receive !== 'granted') {
        setPermissionStatus('denied');
        updateDebug({ permissionResult: permResult.receive, registrationSuccess: false });
        console.log('[Push-iOS] Permission denied');
        return;
      }

      setPermissionStatus('granted');

      console.log('[Push-iOS] Waiting 3s for APNs registration handshake...');
      await sleep(3000);

      let fcmToken: string | null = null;
      let tokenRetryErrorMessage: string | null = null;

      try {
        fcmToken = await waitForFcmToken(FirebaseMessaging, () => listenerToken);
      } catch (directTokenError) {
        tokenRetryErrorMessage = directTokenError instanceof Error
          ? directTokenError.message
          : String(directTokenError);
        console.warn('[Push-iOS] FCM token wait exhausted:', tokenRetryErrorMessage);
      }

      if (!isLikelyFcmToken(fcmToken)) {
        const msg = tokenRetryErrorMessage?.includes('APNs provisioning is still pending')
          ? 'FCM token non disponibile dopo 90s. Prova a chiudere e riaprire la app, poi riprova.'
          : tokenRetryErrorMessage || 'FCM token non disponibile dopo 90s. Prova a chiudere e riaprire la app, poi riprova.';
        updateDebug({ lastError: msg, registrationSuccess: false });
        console.error('[Push-iOS]', msg);
        return;
      }

      console.log(`[Push-iOS] FCM token ready (len=${fcmToken.length})`);

      updateDebug({
        registrationSuccess: true,
        token: fcmToken.substring(0, 20) + '...',
        tokenType: 'FCM',
        lastError: null
      });

      await saveDeviceToken(fcmToken);
      setIsRegistered(true);
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error('[Push-iOS] Error:', msg);
      updateDebug({ registrationSuccess: false, lastError: `iOS: ${msg}` });
    }
  };

  const initializeAndroid = async () => {
    try {
      if (!Capacitor.isPluginAvailable('FirebaseMessaging')) {
        const msg = 'FirebaseMessaging plugin not available (run npx cap sync android and rebuild)';
        console.error('[Push-Android]', msg);
        updateDebug({ registrationSuccess: false, lastError: msg });
        return;
      }

      const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
      await clearPushListeners();

      console.log('[Push-Android] Requesting permission...');
      const permResult = await FirebaseMessaging.requestPermissions();
      console.log('[Push-Android] Permission result:', permResult.receive);
      updateDebug({ permissionResult: permResult.receive });

      if (permResult.receive === 'granted') {
        setPermissionStatus('granted');

        console.log('[Push-Android] Getting FCM token...');
        const tokenResult = await FirebaseMessaging.getToken();
        const fcmToken = tokenResult.token;

        if (!fcmToken) {
          updateDebug({ lastError: 'No FCM token returned', registrationSuccess: false });
          console.error('[Push-Android] No FCM token returned');
          return;
        }

        console.log(`[Push-Android] Token length: ${fcmToken.length}`);
        updateDebug({
          registrationSuccess: true,
          token: fcmToken.substring(0, 20) + '...',
          tokenType: 'FCM',
          lastError: null
        });

        await saveDeviceToken(fcmToken);
        setIsRegistered(true);

        const notificationReceivedListener = await FirebaseMessaging.addListener('notificationReceived', (notification) => {
          console.log('[Push-Android] Notification received:', notification);
          toast.info(notification.notification?.title || 'Nuova notifica', {
            description: notification.notification?.body
          });
        });

        const notificationActionListener = await FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
          console.log('[Push-Android] Notification tapped:', event);
        });

        const tokenListener = await FirebaseMessaging.addListener('tokenReceived', async (event) => {
          console.log('[Push-Android] Token refreshed');
          pushLastKnownFcmToken = event.token;
          await saveDeviceToken(event.token);
        });

        pushListenerHandles = [notificationReceivedListener, notificationActionListener, tokenListener];
      } else {
        setPermissionStatus('denied');
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error('[Push-Android] Error:', msg);
      updateDebug({ registrationSuccess: false, lastError: `Android: ${msg}` });
    }
  };

  const saveDeviceToken = async (token: string) => {
    if (!user) {
      updateDebug({ lastError: 'No user logged in' });
      return;
    }

    try {
      const platform = Capacitor.getPlatform() as 'ios' | 'android';
      pushLastKnownFcmToken = token;

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        const msg = 'No active Supabase session on device';
        console.error('[Push] ' + msg);
        updateDebug({ tokenSaved: false, lastError: msg });
        return;
      }
      
      const { error } = await supabase
        .from('device_tokens')
        .upsert({
          user_id: user.id,
          token,
          platform,
          last_used_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }, { onConflict: 'user_id,token' });

      if (error) {
        console.error('[Push] Error saving token:', error);
        updateDebug({ tokenSaved: false, lastError: error.message });
      } else {
        console.log('[Push] Token saved successfully');
        updateDebug({ tokenSaved: true });
      }
    } catch (error) {
      console.error('[Push] Error in saveDeviceToken:', error);
      updateDebug({ 
        tokenSaved: false, 
        lastError: error instanceof Error ? error.message : String(error) 
      });
    }
  };

  const unregister = async () => {
    if (!user) return;

    try {
      await supabase
        .from('device_tokens')
        .delete()
        .eq('user_id', user.id);

      if (Capacitor.isNativePlatform()) {
        const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
        await clearPushListeners();
        try {
          await FirebaseMessaging.deleteToken();
        } catch (error) {
          console.warn('[Push] deleteToken during unregister failed (non-fatal):', error);
        }
      }

      pushLastKnownFcmToken = null;
      pushPendingForceRefresh = false;
      
      setIsRegistered(false);
      updateDebug({ registrationSuccess: false, token: null, tokenType: null, tokenSaved: false });
      toast.success('Notifiche disabilitate');
    } catch (error) {
      console.error('[Push] Error unregistering:', error);
      toast.error('Errore nella disabilitazione delle notifiche');
    }
  };

  return {
    isSupported,
    isRegistered,
    permissionStatus,
    debugInfo,
    requestPermission: () => initializePushNotifications({ forceRefresh: true }),
    unregister
  };
};